import React from "react";
import { createPortal } from "react-dom";
import { Grid3x3, FileText } from "lucide-react";
import { formatPrice } from "../../lib/matrix";
import { e5Math } from "../../lib/planner";
import ProposalAppendix from "./ProposalAppendix";

const money = (v, symbol, est) => (v === null || v === undefined ? "\u2014" : formatPrice(v, symbol, est));
const signed = (v, symbol, est) => (v === null || v === undefined ? "\u2014" : `${v > 0 ? "+" : v < 0 ? "\u2212" : ""}${formatPrice(Math.abs(v), symbol, est)}`);
const lineName = (l) => l.sku?.name || (l.unlicensed ? "Unlicensed users" : l.from_plan);

/** Proposal content - rendered off-screen for PDF export and inline on the public share page. */
export function ProposalBody({ tenant, scan, plan, plans, rows, priceMode, pricing, scenarioName, shared = false, appendix = null }) {
  const symbol = pricing?.symbol || "$";
  const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const scanDate = scan?.finished_at ? new Date(scan.finished_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
  const changed = plan.lines.filter((l) => l.changed);
  const unchanged = plan.lines.filter((l) => !l.changed && !l.unlicensed);
  const url = `${window.location.origin}`;
  return (
    <>
      <div className="share-top">
        <div className="share-brand">
          <span className="share-mark">
            <Grid3x3 size={20} strokeWidth={2.4} />
          </span>
          <span>
            Microsoft <b>License Hub</b>
          </span>
        </div>
        <div className="share-meta">
          <span className="share-chip">{pricing?.currency || "USD"} · {pricing?.label || "United States"}</span>
          <span className="share-chip">{priceMode === "teams" ? "With Teams" : "No Teams"}</span>
          {shared && <span className="share-chip">Read-only</span>}
          <span className="share-chip muted">{date}</span>
        </div>
      </div>

      <h1 className="share-title">
        <FileText size={34} strokeWidth={2.2} />
        License target-state proposal
      </h1>
      <p className="share-sub">
        {tenant?.name}
        {tenant?.org_display_name ? ` · ${tenant.org_display_name}` : ""}
        {scanDate ? ` · based on the tenant scan of ${scanDate}` : ""}
        {scenarioName ? ` · scenario \u201c${scenarioName}\u201d` : ""}
      </p>

      <div className="prop-kpis">
        <div className="prop-kpi">
          <span>Current / month</span>
          <b className="mono">{money(plan.currentMonthly, symbol, plan.estimated)}</b>
          <small className="mono">{money(plan.currentMonthly * 12, symbol, plan.estimated)} / yr</small>
        </div>
        <div className="prop-kpi">
          <span>Target / month</span>
          <b className="mono">{money(plan.targetMonthly, symbol, plan.estimated)}</b>
          <small className="mono">{money(plan.targetMonthly * 12, symbol, plan.estimated)} / yr</small>
        </div>
        <div className={`prop-kpi primary ${plan.deltaMonthly > 0 ? "up" : plan.deltaMonthly < 0 ? "down" : ""}`}>
          <span>Net delta / month</span>
          <b className="mono">{signed(plan.deltaMonthly, symbol, plan.estimated)}</b>
          <small className="mono">{signed(plan.deltaMonthly * 12, symbol, plan.estimated)} / yr</small>
        </div>
        <div className="prop-kpi">
          <span>Features</span>
          <b>
            <span className="ok">+{plan.gains}</span> {plan.losses ? <span className="warn">{"\u2212"}{plan.losses}</span> : null}
          </b>
          <small className="mono">{plan.retireMonthly > 0 ? `retire \u2212${formatPrice(plan.retireMonthly, symbol)} / mo` : "no add-ons retired"}</small>
        </div>
      </div>

      <h2 className="prop-h2">Proposed moves</h2>
      {changed.length === 0 && <p className="share-sub">No changes - the target state matches the current licensing.</p>}
      {changed.length > 0 && (
        <table className="quote-table prop-table">
          <thead>
            <tr>
              <th>Current</th>
              <th className="num">Seats</th>
              <th>Target</th>
              <th className="num">Unit / user / mo</th>
              <th className="num">License {"\u0394"} / mo</th>
              <th className="num">Retired / mo</th>
              <th className="num">Net {"\u0394"} / mo</th>
            </tr>
          </thead>
          <tbody>
            {changed.map((l) => (
              <tr key={l.from_sku}>
                <td>
                  <span className="share-part">{lineName(l)}</span>
                </td>
                <td className="num mono">{l.seats.toLocaleString()}</td>
                <td>
                  <span className="share-part extra">{l.targetBase?.name || (l.unlicensed ? "\u2014" : "")}</span>
                  {l.addonUnits.map((a) => (
                    <span key={a.id} className="quote-kind">
                      + {a.name}
                    </span>
                  ))}
                </td>
                <td className="num mono">
                  {money(l.currentUnit, symbol)}{" \u2192 "}{money(l.targetUnit, symbol, l.estimated)}
                </td>
                <td className="num mono">{signed(l.licenseDeltaMonthly, symbol, l.estimated)}</td>
                <td className="num mono ok">{l.retireMonthly ? `\u2212${formatPrice(l.retireMonthly, symbol)}` : "\u2014"}</td>
                <td className="num mono total-cell">{signed(l.deltaMonthly, symbol, l.estimated)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td className="num mono">{changed.reduce((s, l) => s + l.seats, 0).toLocaleString()}</td>
              <td />
              <td />
              <td className="num mono">{signed(plan.deltaMonthly + plan.retireMonthly, symbol, plan.estimated)}</td>
              <td className="num mono ok">{plan.retireMonthly ? `\u2212${formatPrice(plan.retireMonthly, symbol)}` : "\u2014"}</td>
              <td className="num mono total">{signed(plan.deltaMonthly, symbol, plan.estimated)}</td>
            </tr>
          </tfoot>
        </table>
      )}
      {unchanged.length > 0 && <p className="prop-note">Unchanged: {unchanged.map((l) => `${lineName(l)} (${l.seats.toLocaleString()} seats)`).join(" · ")}.</p>}

      {changed.map((l) => {
        const math = e5Math({ line: l, plans, rows, priceMode });
        return (
          <section key={l.from_sku} className="prop-move">
            <h3>
              {lineName(l)}{" \u2192 "}{l.targetBase?.name || ""}
              {l.addonUnits.map((a) => ` + ${a.name}`).join("")}
              <span className="mono">
                {l.seats.toLocaleString()} seats · {signed(l.deltaMonthly, symbol, l.estimated)} / mo
              </span>
            </h3>
            {l.remediation && (
              <p className="prop-math">
                {l.remediation.finding ? (
                  <>
                    <b>Closes a security finding:</b> {l.remediation.users.toLocaleString()} users - {l.remediation.label}.
                  </>
                ) : (
                  <>
                    <b>Closes a license gap:</b> {l.remediation.users.toLocaleString()} users are targeted by Conditional Access policies but miss {l.remediation.missing.map((m) => ({ entra_p1: "Entra ID P1", entra_p2: "Entra ID P2", intune_p1: "Intune Plan 1" })[m] || m).join(" + ")}.
                  </>
                )}
              </p>
            )}
            <div className="prop-cols">
              <div>
                <h4>Features gained ({l.gains.length})</h4>
                <p>{l.gains.length ? l.gains.slice(0, 14).map((g) => g.name).join(", ") + (l.gains.length > 14 ? ` and ${l.gains.length - 14} more` : "") : "None"}</p>
                {l.losses.length > 0 && (
                  <>
                    <h4>Features lost ({l.losses.length})</h4>
                    <p>{l.losses.slice(0, 10).map((g) => g.name).join(", ")}</p>
                  </>
                )}
              </div>
              <div>
                <h4>Standalone add-ons to retire</h4>
                {l.retire.length === 0 && <p>None detected for these users.</p>}
                {l.retire.length > 0 && (
                  <ul>
                    {l.retire.map((r) => (
                      <li key={r.sku.sku_id}>
                        <span>
                          {r.sku.name} · {r.seats.toLocaleString()} seats
                        </span>
                        <b className="mono ok">{r.monthly === null ? "price n/a" : `\u2212${formatPrice(r.monthly, symbol, r.estimated)} / mo`}</b>
                      </li>
                    ))}
                  </ul>
                )}
                {math && (
                  <p className="prop-math">
                    <b>E5 add-on math:</b> {math.bundle.label} {money(math.bundle.unit, symbol)} vs {math.alt.label} {money(math.alt.unit, symbol)} per user.{" "}
                    {math.cheaper === "alt" && `${math.alt.label} is ${formatPrice(math.bundle.unit - math.alt.unit, symbol)} / user cheaper${math.altOnly.length ? ` and adds ${math.altOnly.length} features` : ""}.`}
                    {math.cheaper === "bundle" && `${math.bundle.label} is ${formatPrice(math.alt.unit - math.bundle.unit, symbol)} / user cheaper${math.bundleOnly.length ? ` and includes ${math.bundleOnly.length} features the alternative lacks` : ""}.`}
                    {math.cheaper === "same" && "Both options cost the same per user."}
                  </p>
                )}
              </div>
            </div>
          </section>
        );
      })}

      <ProposalAppendix lists={appendix} />

      <div className="share-foot">
        <span>
          {pricing?.note}. Annual = monthly x 12. {plan.estimated ? "\u2248 = estimated from US list price. " : ""}
          {plan.unpriced.length ? `${plan.unpriced.length} SKU(s) without a list price are excluded from totals. ` : ""}
          Add-on retirement is estimated from users holding both the base suite and the add-on. List prices exclude taxes and partner discounts - verify with Microsoft before purchasing.
        </span>
        <span className="share-url">{url}</span>
      </div>
    </>
  );
}

// Off-screen A4-width proposal (sliced into PDF pages by downloadDocumentPdf).
export default function ProposalCard({ cardRef, ...props }) {
  return createPortal(
    <div className="share-stage" aria-hidden="true">
      <div className="share-card proposal" ref={cardRef} data-testid="proposal-card">
        <ProposalBody {...props} />
      </div>
    </div>,
    document.body
  );
}
